import { useState, memo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import FormControl from '@zenbusiness/zui/FormControl';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';
import { v1 as uuidv1 } from 'uuid';

import AutoCompleteInput from '#client/components/AutoCompleteInput';
import { ENTITY_TYPE_OPTIONS } from '#client/constants/additionalInfo';
import addToString from '#client/utils/addToString';

import { StyledPopper } from './components/IndustryUIFieldVerticalList.styles';

import { isOptionEqualToValue, renderOption } from './utils/verticalListOptions';

const options = addToString(ENTITY_TYPE_OPTIONS);

const EntityTypeUIField = ({
  field = { setSaved: () => {}, setLoading: () => {} },
  dataChangeHandler = () => {},
  disabled,
  data
}) => {
  const [selectedOption, setSelectedOption] = useState(
    options.find((option) => option.value === data?.entityType)
  );

  const trackEntityTypeChangeClick = useButtonPressedCallback({
    elementName: 'banking_collect_info',
    appPageName: 'banking_application'
  });

  const onDataChanged = (event, option) => {
    if (option) {
      trackEntityTypeChangeClick(event);
      field.setLoading();
    }
    dataChangeHandler(
      !!option || !!selectedOption?.value,
      { entityType: option?.value },
      (forceUpdate, { isSave, isError }) => {
        field.setSaved(isSave, isError);
        forceUpdate();
      },
      field.key
    );
  };

  const handleChangeOption = (event, option) => {
    setSelectedOption(option);
    onDataChanged(event, option);
  };

  return (
    <FormControl
      fullWidth
      sx={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 3 }}
      variant="outlined"
    >
      <AutoCompleteInput
        autoHighlight
        blurOnSelect
        disabled={disabled}
        getOptionLabel={(option) => option.toString()}
        isOptionEqualToValue={isOptionEqualToValue}
        ListboxProps={{
          style: {
            maxHeight: '400px',
            py: 2,
            borderRadius: 2
          }
        }}
        noOptionsText="No results. Try a new keyword."
        onChange={handleChangeOption}
        openOnFocus
        options={options}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              name: uuidv1()
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              ...params.InputProps
            }}
            label="Select entity type"
          />
        )}
        renderOption={renderOption}
        value={selectedOption || null}
      />
    </FormControl>
  );
};

EntityTypeUIField.propTypes = {
  data: PropTypes.shape({
    entityType: PropTypes.string
  }),
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({
    setSaved: PropTypes.func,
    setLoading: PropTypes.func,
    key: PropTypes.string
  }),
  disabled: PropTypes.bool,
  useFor: PropTypes.string
};

function areEqual(preProp, nextProp) {
  return (
    preProp.field === nextProp.field &&
    preProp.data === nextProp.data &&
    preProp.disabled === nextProp.disabled
  );
}

export default memo(EntityTypeUIField, areEqual);
